import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { db } from '@/firebase/config';
import { collection, query, where, onSnapshot, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, ShieldAlert } from 'lucide-react';

const LiveAccessListener = () => {
  const { toast } = useToast();
  const processedRequests = useRef<Set<string>>(new Set());
  
  const [pendingRequest, setPendingRequest] = useState<{ id: string; targetRole: string } | null>(null);
  const [processing, setProcessing] = useState(false);

  const user = useSelector((state: any) => state.users?.currentUser || {});
  const { selectedAffiliation } = user;

  const isSchoolAdmin = selectedAffiliation?.role === 'admin' && selectedAffiliation?.schoolID;
  const schoolID = selectedAffiliation?.schoolID;

  useEffect(() => {
    if (!isSchoolAdmin || !schoolID) return;

    const requestsRef = collection(db, `schools/${schoolID}/liveSupportRequests`);
    const q = query(
      requestsRef, 
      where("status", "==", "pending")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === "added") {
          const data = change.doc.data();
          const requestId = change.doc.id;
          
          if (!processedRequests.current.has(requestId)) {
            processedRequests.current.add(requestId);
            
            // Set as the current pending request to show the dialog
            // We prioritize the most recent one if multiple come in, or queue them?
            // For simplicity, we just show the latest one.
            setPendingRequest({
                id: requestId,
                targetRole: data.targetRole
            });
          }
        }
      });
    });

    return () => unsubscribe();
  }, [isSchoolAdmin, schoolID]);

  const handleResponse = async (status: 'approved' | 'rejected') => {
    if (!schoolID || !pendingRequest) return;
    
    setProcessing(true);
    try {
        await updateDoc(doc(db, `schools/${schoolID}/liveSupportRequests`, pendingRequest.id), {
            status,
            respondedAt: Timestamp.now(),
            respondedBy: user.email || 'admin'
        });
        
        toast({
            title: status === 'approved' ? "Access Granted" : "Access Denied",
            description: `You have ${status} the customer support access request.`,
            variant: status === 'approved' ? "default" : "destructive",
        });
        
        setPendingRequest(null);
    } catch (error) {
        console.error("Error updating request:", error);
        toast({
            title: "Error",
            description: "Failed to process response.",
            variant: "destructive"
        });
    } finally {
        setProcessing(false);
    }
  };

  if (!pendingRequest) return null;

  return (
    <AlertDialog open={!!pendingRequest} onOpenChange={() => {}}>
      <AlertDialogContent className="border-l-4 border-l-amber-500 max-w-md mx-auto">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-amber-100 rounded-full">
                <ShieldAlert className="h-6 w-6 text-amber-600" />
            </div>
            <AlertDialogTitle className="text-xl">Authentication Required</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-base text-slate-700 dark:text-slate-300">
            <strong>Customer Support</strong> is requesting live access to View As <strong>{pendingRequest.targetRole}</strong> for troubleshooting.
            <br/><br/>
            Do you want to grant this temporary access?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="mt-4 flex-col sm:flex-row gap-2">
          <AlertDialogCancel 
            disabled={processing}
            onClick={() => handleResponse('rejected')}
            className="w-full sm:w-auto text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
          >
            Deny Access
          </AlertDialogCancel>
          <AlertDialogAction 
             disabled={processing}
             onClick={(e) => {
                e.preventDefault();
                handleResponse('approved');
             }}
             className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white"
          >
             {processing ? (
                <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                </>
             ) : (
                'Grant Access'
             )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default LiveAccessListener;
